import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="flex items-center gap-2 cursor-pointer group"
          >
            <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 font-heading font-bold text-lg md:text-xl transition-transform duration-300 group-hover:scale-105">
              伦
            </div>
            <span className="font-heading text-lg md:text-xl font-semibold text-foreground-950">
              {t('site_name')}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isActive('/')
                  ? 'text-primary-600 bg-primary-100/70'
                  : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
              }`}
            >
              {t('nav_home')}
            </Link>
            <Link
              to="/category/ethical-theory"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isActive('/category')
                  ? 'text-primary-600 bg-primary-100/70'
                  : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
              }`}
            >
              {t('nav_categories')}
            </Link>
            <Link
              to="/about"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isActive('/about')
                  ? 'text-primary-600 bg-primary-100/70'
                  : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
              }`}
            >
              {t('nav_about')}
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/category/ethics-assessment"
              className="px-4 py-2 rounded-md text-sm font-medium bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              {t('hero_cta_assessment')}
            </Link>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-3 flex flex-col gap-1">
            <Link
              to="/"
              className={`px-4 py-3 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isActive('/')
                  ? 'text-primary-600 bg-primary-100/70'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              {t('nav_home')}
            </Link>
            <Link
              to="/category/ethical-theory"
              className={`px-4 py-3 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isActive('/category')
                  ? 'text-primary-600 bg-primary-100/70'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              {t('nav_categories')}
            </Link>
            <Link
              to="/about"
              className={`px-4 py-3 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isActive('/about')
                  ? 'text-primary-600 bg-primary-100/70'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              {t('nav_about')}
            </Link>
            <Link
              to="/category/ethics-assessment"
              className="px-4 py-3 rounded-md text-sm font-medium bg-accent-500 text-background-50 text-center mt-1 whitespace-nowrap cursor-pointer"
            >
              {t('hero_cta_assessment')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}